import { useQuery } from "@apollo/client";
import { Radio, Stack, Typography } from "@mui/material";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { EpisodicEpaFilingFieldsFragment, WasteEpisodicEventType } from "generated-graphql/graphql";
import { ReactNode, useEffect, useRef, useState } from "react";

import { EPISODIC_EPA_FILING_CANDIDATES_QUERY } from "./api";
import {
  describeEpaFiling,
  describeEpaFilingReceipt,
  EPA_FILING_TYPE_MISMATCH_HINT,
  EpaFilingKeyInput,
  sameEpaFilingKey,
} from "./epaFilingDisplay";

export type EpaFilingCandidate = { typeMatches: boolean; filing: EpisodicEpaFilingFieldsFragment };

const EPA_RECORDS_TOOLTIP =
  "These are episodic filings Encamp has pulled from EPA's RCRAInfo system for this facility's EPA IDs. Choosing one uses EPA's own record of the filing as proof, so no copy needs to be uploaded.";

function heading(count: number): string {
  if (count === 0) {
    return "EPA records";
  }
  return `EPA records (${count} ${count === 1 ? "filing" : "filings"} found)`;
}

/**
 * Fires `onChoose` once for a lone type-matching candidate, when the caller
 * asked for it and nothing is selected yet. Once only: after the user clears
 * that selection, the same lone candidate must not snap back.
 */
function useAutoSelectSingle(
  enabled: boolean,
  candidates: EpaFilingCandidate[],
  selectedKey: EpaFilingKeyInput | null,
  onChoose: (candidate: EpaFilingCandidate) => void
) {
  const done = useRef(false);
  const lone = candidates.length === 1 && candidates[0].typeMatches ? candidates[0] : null;
  useEffect(() => {
    if (!enabled || done.current || lone == null || selectedKey != null) {
      return;
    }
    done.current = true;
    onChoose(lone);
  }, [enabled, lone, selectedKey, onChoose]);
}

/**
 * The evidence block of the notice dialogs (`RecordNoticeDialog`,
 * `ExternalFilingSection`): EPA-recorded episodic filings for the facility
 * near the event's start date as a radio list, with the caller's upload
 * field folded in beneath them. While EPA has candidates the upload is the
 * last radio option (choosing it also clears any chosen record); it stays
 * open when the caller already has something to show there
 * (`keepUploadOpen`). When EPA has nothing, or the lookup failed, a quiet
 * caption says so and the upload shows directly — never "EPA has nothing"
 * for a lookup that did not run, and no alert styling, since uploading is a
 * normal path rather than a failure.
 */
export function EpaFilingSuggestions({
  facilityId,
  eventType,
  startDate,
  excludeEventId = null,
  selectedKey = null,
  autoSelectSingle = false,
  keepUploadOpen = false,
  onChoose,
  onClear,
  uploadField,
  busy = false,
}: {
  facilityId: string;
  eventType: WasteEpisodicEventType;
  startDate: string;
  excludeEventId?: string | null;
  selectedKey?: EpaFilingKeyInput | null;
  /** Choose a lone type-matching candidate without a click. Callers set
   * this only where the user has already committed to recording a filing
   * and nothing else is attached yet. */
  autoSelectSingle?: boolean;
  /** Keep the upload visible beside the candidates: the caller has a picked
   * file, an attached document, or a validation error living in it. */
  keepUploadOpen?: boolean;
  onChoose: (candidate: EpaFilingCandidate) => void;
  /** Detach the chosen record; called when the upload option is chosen. */
  onClear: () => void;
  /** The caller's upload field. `null` means no upload path yet (the
   * declaration section withholds it until a filing date is entered). */
  uploadField: ReactNode;
  busy?: boolean;
}) {
  const { data, loading, error } = useQuery(EPISODIC_EPA_FILING_CANDIDATES_QUERY, {
    variables: { facilityId, type: eventType, startDate, excludeEventId },
  });
  const candidates = data?.episodicEpaFilingCandidates ?? [];
  useAutoSelectSingle(autoSelectSingle, candidates, selectedKey, onChoose);
  const [uploadRevealed, setUploadRevealed] = useState(false);

  const hasCandidates = candidates.length > 0;
  const settled = !loading || error != null;
  const uploadChosen = selectedKey == null && (uploadRevealed || keepUploadOpen);
  const showUpload = hasCandidates ? uploadChosen : settled;
  const chooseUpload = () => {
    onClear();
    setUploadRevealed(true);
  };
  const chooseCandidate = (candidate: EpaFilingCandidate) => {
    setUploadRevealed(false);
    onChoose(candidate);
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          {heading(candidates.length)}
        </Typography>
        <InfoIconTooltip tooltip={EPA_RECORDS_TOOLTIP} ariaLabel="About EPA records" />
      </Stack>
      {hasCandidates ? (
        candidates.map((candidate) => (
          <CandidateRow
            key={`${candidate.filing.handlerId}-${candidate.filing.activityLocation}-${candidate.filing.seqNumber}`}
            candidate={candidate}
            checked={sameEpaFilingKey(selectedKey, candidate.filing)}
            onChoose={chooseCandidate}
            busy={busy}
          />
        ))
      ) : (
        <LookupCaption loading={loading} failed={error != null} />
      )}
      {hasCandidates && uploadField != null && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Radio
            size="small"
            name="epaFilingCandidate"
            checked={uploadChosen}
            onChange={chooseUpload}
            disabled={busy}
            inputProps={{ "aria-label": "Upload a copy of the filed form" }}
            sx={{ p: 0 }}
          />
          <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2">Upload a copy of the filed form</Typography>
            {showUpload && uploadField}
          </Stack>
        </Stack>
      )}
      {!hasCandidates && showUpload && uploadField}
    </Stack>
  );
}

function LookupCaption({ loading, failed }: { loading: boolean; failed: boolean }) {
  let text: string;
  if (failed) {
    text = "We couldn't check EPA's records right now. Upload a copy of the filed form instead.";
  } else if (loading) {
    text = "Checking EPA's records…";
  } else {
    text =
      "EPA has no matching episodic filing on record for this facility near this date. Upload a copy of the filed form, or contact Encamp Support if you don't have one.";
  }
  return (
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  );
}

function CandidateRow({
  candidate,
  checked,
  onChoose,
  busy,
}: {
  candidate: EpaFilingCandidate;
  checked: boolean;
  onChoose: (candidate: EpaFilingCandidate) => void;
  busy: boolean;
}) {
  const { filing } = candidate;
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Radio
        size="small"
        name="epaFilingCandidate"
        checked={checked}
        onChange={() => onChoose(candidate)}
        disabled={busy}
        inputProps={{ "aria-label": describeEpaFiling(filing) }}
        sx={{ p: 0 }}
      />
      <Stack>
        <Typography variant="body2">{describeEpaFiling(filing)}</Typography>
        <Typography variant="caption" color="text.secondary">
          {!candidate.typeMatches && (
            <>
              <Typography component="span" variant="caption">
                {EPA_FILING_TYPE_MISMATCH_HINT}
              </Typography>
              {" · "}
            </>
          )}
          {describeEpaFilingReceipt(filing)}
        </Typography>
      </Stack>
    </Stack>
  );
}
